import React, { useState, useEffect, useRef } from 'react';
import {
  Activity,
  Radio,
  PhoneCall,
  MessageSquare,
  ShoppingCart,
  Globe,
  RefreshCw,
  AlertTriangle,
  AlertOctagon,
  CheckCircle2,
  Clock,
  Zap,
  Search,
  Pause,
  Play,
  Trash2,
  SlidersHorizontal,
  ChevronRight,
  X,
  Copy,
  Check,
  Server,
  Key,
  ShieldAlert,
  ArrowRight
} from 'lucide-react';

export interface TelemetryEvent {
  id: string;
  timestamp: string;
  channel: 'whatsapp' | 'sip_voice' | 'web_live' | 'pos' | 'personal_luna' | 'public_web' | 'widget';
  tenantId?: string;
  tenantName?: string;
  storeId?: number;
  storeName?: string;
  callerId?: string;
  clientIp: string;
  device?: string;
  userAgent?: string;
  promptPreview?: string;
  responsePreview?: string;
  keyMode: 'TENANT_DEDICATED' | 'PLATFORM_MASTER' | 'AUTO_SWITCH_FALLBACK';
  autoSwitched: boolean;
  fallbackReason?: string;
  status: 'active' | 'success' | 'fallback' | 'overload_429' | 'error';
  latencyMs: number;
  tokensUsed: number;
  errorMessage?: string;
}

interface TelemetrySummary {
  activeStreams: number;
  todayTotal: number;
  lifetimeTotal?: number;
  lifetimeTokens?: number;
  lifetimeFallbacks?: number;
  lifetimeErrors?: number;
  allTimeBuffered: number;
  todayFallbacks: number;
  todayErrors: number;
  avgLatencyMs: number;
  channels: Record<string, number>;
  keyModes: Record<string, number>;
}

interface Props {
  authHeader: () => Record<string, string>;
  adminToken?: string;
}

export const LunaLiveMonitorHUD: React.FC<Props> = ({ authHeader, adminToken }) => {
  const [events, setEvents] = useState<TelemetryEvent[]>([]);
  const [summary, setSummary] = useState<TelemetrySummary | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedKeyMode, setSelectedKeyMode] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [inspectingEvent, setInspectingEvent] = useState<TelemetryEvent | null>(null);
  const [transcriptData, setTranscriptData] = useState<any | null>(null);
  const [transcriptLoading, setTranscriptLoading] = useState<boolean>(false);
  const [copied, setCopied] = useState(false);
  const [nowTime, setNowTime] = useState<number>(Date.now());

  // 1-second live clock tick for real-time running stopwatches on active sessions
  useEffect(() => {
    const timer = setInterval(() => setNowTime(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch full conversation transcript when inspecting an event
  useEffect(() => {
    if (!inspectingEvent?.id) {
      setTranscriptData(null);
      setTranscriptLoading(false);
      return;
    }
    let isCancelled = false;
    setTranscriptLoading(true);
    const token = adminToken || localStorage.getItem('kangavena_admin_token') || sessionStorage.getItem('adminToken') || '';
    const headers: Record<string, string> = {};
    if (authHeader) headers['Authorization'] = authHeader;
    else if (token) headers['Authorization'] = `Bearer ${token}`;

    fetch(`/api/admin/luna/session-transcript/${encodeURIComponent(inspectingEvent.id)}`, { headers })
      .then(res => res.json())
      .then(data => {
        if (!isCancelled) {
          if (data.success && data.conversation) {
            setTranscriptData(data.conversation);
          } else {
            setTranscriptData(null);
          }
        }
      })
      .catch(() => {
        if (!isCancelled) setTranscriptData(null);
      })
      .finally(() => {
        if (!isCancelled) setTranscriptLoading(false);
      });

    return () => {
      isCancelled = true;
    };
  }, [inspectingEvent?.id, adminToken, authHeader]);

  const formatDurationOrLatency = (event: TelemetryEvent) => {
    if (event.status === 'active') {
      const elapsedSec = Math.max(0, Math.floor((nowTime - new Date(event.timestamp).getTime()) / 1000));
      const m = Math.floor(elapsedSec / 60);
      const s = elapsedSec % 60;
      return (
        <span className="inline-flex items-center gap-1 font-bold text-cyan-400 animate-pulse">
          <Clock size={11} className="text-cyan-300 animate-spin" />
          <span>{m > 0 ? `${m}m ${s}s` : `${s}s`} live</span>
        </span>
      );
    }
    if (event.latencyMs >= 1000) {
      const totalSec = Math.round(event.latencyMs / 1000);
      const m = Math.floor(totalSec / 60);
      const s = totalSec % 60;
      return (
        <span className="font-bold text-slate-200" title={`${event.latencyMs.toLocaleString()}ms total duration`}>
          {m > 0 ? `${m}m ${s}s` : `${s}s`}
        </span>
      );
    }
    if (event.latencyMs > 0) {
      return (
        <span className="font-bold text-slate-300">
          {event.latencyMs}ms
        </span>
      );
    }
    return <span className="text-slate-500">—</span>;
  };

  const eventSourceRef = useRef<EventSource | null>(null);
  const isPausedRef = useRef(isPaused);
  isPausedRef.current = isPaused;

  // Fetch initial summary & events
  const fetchSummary = async () => {
    try {
      const res = await fetch('/api/admin/luna-telemetry/summary', { headers: authHeader() });
      if (res.ok) {
        const data = await res.json();
        setSummary(data);
      }
    } catch (_) {}
  };

  const fetchRecentEvents = async () => {
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (selectedKeyMode !== 'all') params.append('keyMode', selectedKeyMode);
      if (selectedChannel !== 'all') params.append('channel', selectedChannel);
      if (selectedStatus !== 'all') params.append('status', selectedStatus);

      const res = await fetch(`/api/admin/luna-telemetry/events?${params.toString()}`, { headers: authHeader() });
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data.events)) {
          setEvents(data.events);
        }
      }
    } catch (_) {}
  };

  // Re-fetch when persistent filter changes
  useEffect(() => {
    fetchRecentEvents();
  }, [selectedKeyMode, selectedChannel, selectedStatus]);

  // Setup Real-time SSE Stream
  useEffect(() => {
    fetchSummary();
    fetchRecentEvents();

    const token = adminToken || localStorage.getItem('kangavena_admin_token') || '';
    const sseUrl = `/api/admin/luna-telemetry/stream?token=${encodeURIComponent(token)}`;

    const es = new EventSource(sseUrl);
    eventSourceRef.current = es;

    es.onopen = () => {
      setIsConnected(true);
    };

    es.onerror = () => {
      setIsConnected(false);
    };

    es.onmessage = (e) => {
      try {
        const payload = JSON.parse(e.data);
        if (payload.type === 'connected') {
          setIsConnected(true);
          if (Array.isArray(payload.recentEvents) && events.length === 0) {
            setEvents(payload.recentEvents);
          }
        } else if (payload.type === 'luna_telemetry_event' && payload.event) {
          if (!isPausedRef.current) {
            setEvents(prev => [payload.event, ...prev.slice(0, 499)]);
            fetchSummary();
          }
        } else if (payload.type === 'luna_telemetry_update' && payload.event) {
          if (!isPausedRef.current) {
            setEvents(prev => prev.map(item => item.id === payload.event.id ? payload.event : item));
            fetchSummary();
          }
        }
      } catch (_) {}
    };

    const summaryInterval = setInterval(fetchSummary, 10000);

    return () => {
      es.close();
      clearInterval(summaryInterval);
    };
  }, [adminToken]);

  // Filter events
  const filteredEvents = events.filter(e => {
    if (selectedChannel !== 'all' && e.channel !== selectedChannel) return false;
    if (selectedStatus !== 'all') {
      if (selectedStatus === 'fallback' && !e.autoSwitched && e.status !== 'fallback') return false;
      if (selectedStatus === 'error' && e.status !== 'error' && e.status !== 'overload_429') return false;
      if (selectedStatus === 'success' && e.status !== 'success') return false;
    }
    if (selectedKeyMode !== 'all') {
      if (selectedKeyMode === 'AUTO_SWITCH_FALLBACK') {
        if (!e.autoSwitched && e.keyMode !== 'AUTO_SWITCH_FALLBACK') return false;
      } else if (selectedKeyMode === 'TENANT_DEDICATED') {
        if (e.keyMode !== 'TENANT_DEDICATED' || e.autoSwitched) return false;
      } else if (selectedKeyMode === 'PLATFORM_MASTER') {
        if (e.keyMode !== 'PLATFORM_MASTER' || e.autoSwitched) return false;
      }
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const match =
        (e.tenantName && e.tenantName.toLowerCase().includes(q)) ||
        (e.tenantId && e.tenantId.toLowerCase().includes(q)) ||
        (e.callerId && e.callerId.toLowerCase().includes(q)) ||
        (e.clientIp && e.clientIp.includes(q)) ||
        (e.promptPreview && e.promptPreview.toLowerCase().includes(q));
      if (!match) return false;
    }
    return true;
  });

  const getChannelBadge = (ch: string) => {
    switch (ch) {
      case 'whatsapp':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 font-mono text-[11px] font-bold">
            <Radio size={12} className="text-emerald-400" /> WhatsApp
          </span>
        );
      case 'sip_voice':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-cyan-500/15 border border-cyan-500/30 text-cyan-300 font-mono text-[11px] font-bold">
            <PhoneCall size={12} className="text-cyan-400 animate-pulse" /> SIP Live Call
          </span>
        );
      case 'pos':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-amber-500/15 border border-amber-500/30 text-amber-300 font-mono text-[11px] font-bold">
            <ShoppingCart size={12} className="text-amber-400" /> POS Cashier
          </span>
        );
      case 'personal_luna':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-indigo-500/15 border border-indigo-500/30 text-indigo-300 font-mono text-[11px] font-bold">
            <MessageSquare size={12} className="text-indigo-400" /> Personal Luna
          </span>
        );
      case 'public_web':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-blue-500/15 border border-blue-500/30 text-blue-300 font-mono text-[11px] font-bold">
            <Globe size={12} className="text-blue-400" /> Public Web
          </span>
        );
      case 'widget':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-teal-500/15 border border-teal-500/30 text-teal-300 font-mono text-[11px] font-bold">
            <Globe size={12} className="text-teal-400" /> Tenant Web
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-purple-500/15 border border-purple-500/30 text-purple-300 font-mono text-[11px] font-bold">
            <Activity size={12} className="text-purple-400" /> {ch}
          </span>
        );
    }
  };

  const getStatusBadge = (event: TelemetryEvent) => {
    if (event.autoSwitched || event.status === 'fallback') {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-amber-500/20 border border-amber-500/50 text-amber-300 font-mono text-[10px] font-black uppercase tracking-wider animate-pulse shadow-[0_0_10px_rgba(245,158,11,0.3)]">
          <RefreshCw size={11} className="text-amber-400" /> AUTO-SWITCHED
        </span>
      );
    }
    if (event.status === 'overload_429') {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-rose-500/20 border border-rose-500/50 text-rose-300 font-mono text-[10px] font-black uppercase tracking-wider shadow-[0_0_10px_rgba(244,63,94,0.3)]">
          <AlertOctagon size={11} className="text-rose-400" /> 429 OVERLOAD
        </span>
      );
    }
    if (event.status === 'error') {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-rose-500/20 border border-rose-500/40 text-rose-400 font-mono text-[10px] font-black uppercase tracking-wider">
          <AlertTriangle size={11} className="text-rose-400" /> ERROR
        </span>
      );
    }
    if (event.status === 'active') {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-cyan-500/20 border border-cyan-400 text-cyan-300 font-mono text-[10px] font-black uppercase tracking-wider animate-pulse">
          <Radio size={11} className="text-cyan-400 animate-spin" /> ACTIVE
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 font-mono text-[10px] font-bold">
        <CheckCircle2 size={11} className="text-emerald-400" /> OK
      </span>
    );
  };

  const copyEventJson = () => {
    if (!inspectingEvent) return;
    navigator.clipboard.writeText(JSON.stringify(inspectingEvent, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-6">
      {/* ── TOP KPI TELEMETRY HUD ── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
        {/* Connection & Live Concurrent Streams */}
        <div className="bg-gradient-to-br from-[#0B132B] to-[#090D18] border border-cyan-500/30 rounded-2xl p-3.5 relative overflow-hidden shadow-lg">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] font-mono font-bold text-cyan-400 uppercase tracking-widest flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-full ${isConnected ? 'bg-emerald-400 animate-ping' : 'bg-rose-500'}`} />
              {isConnected ? 'ONLINE' : 'CONNECT...'}
            </span>
            <Radio size={15} className="text-cyan-400" />
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-2xl font-black font-mono text-white">
              {summary?.activeStreams ?? 0}
            </span>
            <span className="text-xs font-mono text-cyan-400/80 font-bold">active</span>
          </div>
          <p className="text-[10px] text-slate-400 mt-1 font-mono truncate">Live voice/streams</p>
        </div>

        {/* Requests Today (All) */}
        <button
          onClick={() => { setSelectedKeyMode('all'); setSelectedStatus('all'); }}
          className={`text-left bg-[#090D18] border rounded-2xl p-3.5 shadow-lg transition-all ${
            selectedKeyMode === 'all' && selectedStatus === 'all'
              ? 'border-white/40 ring-1 ring-white/30'
              : 'border-white/10 hover:border-white/25'
          }`}
          title="Click to view all requests"
        >
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest">
              REQUESTS TODAY
            </span>
            <Zap size={15} className="text-emerald-400" />
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-2xl font-black font-mono text-white">
              {summary?.todayTotal ?? events.length}
            </span>
            <span className="text-xs font-mono text-emerald-400 font-bold">today</span>
          </div>
          <p className="text-[10px] text-slate-400 mt-1 font-mono truncate">
            Lifetime: <strong className="text-cyan-400 font-bold">{summary?.lifetimeTotal ?? summary?.todayTotal ?? events.length}</strong> all-time
          </p>
        </button>

        {/* Tenant Admin Own Keys (BYOK) */}
        <button
          onClick={() => setSelectedKeyMode(prev => prev === 'TENANT_DEDICATED' ? 'all' : 'TENANT_DEDICATED')}
          className={`text-left bg-gradient-to-br from-emerald-950/30 to-[#090D18] border rounded-2xl p-3.5 shadow-lg transition-all ${
            selectedKeyMode === 'TENANT_DEDICATED'
              ? 'border-emerald-400 ring-2 ring-emerald-500/50 shadow-[0_0_20px_rgba(16,185,129,0.3)]'
              : 'border-emerald-500/30 hover:border-emerald-400/60'
          }`}
          title="Click to filter by Tenant Admin Own Keys"
        >
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] font-mono font-bold text-emerald-400 uppercase tracking-widest flex items-center gap-1">
              <Key size={12} className="text-emerald-400" /> TENANT KEYS
            </span>
            <span className="px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 font-mono text-[9px] font-black">
              BYOK
            </span>
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-2xl font-black font-mono text-emerald-300">
              {summary?.keyModes?.TENANT_DEDICATED ?? 0}
            </span>
            <span className="text-xs font-mono text-emerald-400/80 font-bold">reqs</span>
          </div>
          <p className="text-[10px] text-slate-400 mt-1 font-mono truncate">Tenant own accounts</p>
        </button>

        {/* Platform Master Key */}
        <button
          onClick={() => setSelectedKeyMode(prev => prev === 'PLATFORM_MASTER' ? 'all' : 'PLATFORM_MASTER')}
          className={`text-left bg-gradient-to-br from-cyan-950/30 to-[#090D18] border rounded-2xl p-3.5 shadow-lg transition-all ${
            selectedKeyMode === 'PLATFORM_MASTER'
              ? 'border-cyan-400 ring-2 ring-cyan-500/50 shadow-[0_0_20px_rgba(6,182,212,0.3)]'
              : 'border-cyan-500/30 hover:border-cyan-400/60'
          }`}
          title="Click to filter by Platform Master Key"
        >
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] font-mono font-bold text-cyan-400 uppercase tracking-widest flex items-center gap-1">
              <Zap size={12} className="text-cyan-400" /> MASTER KEY
            </span>
            <span className="px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-300 font-mono text-[9px] font-black">
              SHARED
            </span>
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-2xl font-black font-mono text-cyan-300">
              {summary?.keyModes?.PLATFORM_MASTER ?? 0}
            </span>
            <span className="text-xs font-mono text-cyan-400/80 font-bold">reqs</span>
          </div>
          <p className="text-[10px] text-slate-400 mt-1 font-mono truncate">Platform main core</p>
        </button>

        {/* Auto-Switched Fallbacks */}
        <button
          onClick={() => setSelectedKeyMode(prev => prev === 'AUTO_SWITCH_FALLBACK' ? 'all' : 'AUTO_SWITCH_FALLBACK')}
          className={`text-left bg-gradient-to-br from-amber-950/30 to-[#090D18] border rounded-2xl p-3.5 shadow-lg transition-all ${
            selectedKeyMode === 'AUTO_SWITCH_FALLBACK'
              ? 'border-amber-400 ring-2 ring-amber-500/50 shadow-[0_0_20px_rgba(245,158,11,0.3)]'
              : 'border-amber-500/40 hover:border-amber-400/70'
          }`}
          title="Click to filter by Auto-Switched Fallbacks"
        >
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] font-mono font-bold text-amber-400 uppercase tracking-widest flex items-center gap-1">
              <RefreshCw size={12} className="text-amber-400" /> AUTO-SWITCH
            </span>
            <span className="px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 font-mono text-[9px] font-black">
              HEAL
            </span>
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-2xl font-black font-mono text-amber-300">
              {summary?.todayFallbacks ?? 0}
            </span>
            <span className="text-xs font-mono text-amber-400/80 font-bold">today</span>
          </div>
          <p className="text-[10px] text-slate-400 mt-1 font-mono truncate">
            Lifetime: <strong className="text-amber-400 font-bold">{summary?.lifetimeFallbacks ?? 0}</strong> fallbacks
          </p>
        </button>

        {/* Overload & Errors */}
        <button
          onClick={() => setSelectedStatus(prev => prev === 'error' ? 'all' : 'error')}
          className={`text-left bg-gradient-to-br from-rose-950/30 to-[#090D18] border rounded-2xl p-3.5 shadow-lg transition-all ${
            selectedStatus === 'error'
              ? 'border-rose-400 ring-2 ring-rose-500/50 shadow-[0_0_20px_rgba(244,63,94,0.3)]'
              : 'border-rose-500/40 hover:border-rose-400/70'
          }`}
          title="Click to filter by Errors and 429 Overload"
        >
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] font-mono font-bold text-rose-400 uppercase tracking-widest flex items-center gap-1">
              <ShieldAlert size={12} className="text-rose-400" /> 429 & ERRORS
            </span>
            <span className="px-1.5 py-0.5 rounded bg-rose-500/20 text-rose-300 font-mono text-[9px] font-black">
              QUOTA
            </span>
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-2xl font-black font-mono text-rose-300">
              {summary?.todayErrors ?? 0}
            </span>
            <span className="text-xs font-mono text-rose-400/80 font-bold">today</span>
          </div>
          <p className="text-[10px] text-slate-400 mt-1 font-mono truncate">
            Lifetime: <strong className="text-rose-400 font-bold">{summary?.lifetimeErrors ?? 0}</strong> issues
          </p>
        </button>

        {/* Avg Latency */}
        <div className="bg-[#090D18] border border-white/10 rounded-2xl p-3.5 shadow-lg">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest">
              AVG LATENCY
            </span>
            <Clock size={15} className="text-cyan-400" />
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-2xl font-black font-mono text-cyan-300">
              {summary?.avgLatencyMs ?? 0}ms
            </span>
            <span className="text-xs font-mono text-slate-400">roundtrip</span>
          </div>
          <p className="text-[10px] text-slate-400 mt-1 font-mono truncate">Unified Gemini Live</p>
        </div>
      </div>

      {/* ── FILTER & CONTROL BAR ── */}
      <div className="bg-[#090D18] border border-cyan-500/20 rounded-2xl p-4 space-y-3 shadow-md">
        {/* Row 1: AI Key Source Filter & Search & Stream Controls */}
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-3">
          {/* Key Mode Filter Pills */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[11px] font-mono font-bold text-slate-400 mr-1 flex items-center gap-1">
              <Key size={13} className="text-amber-400" /> AI Key:
            </span>
            {[
              { id: 'all', label: 'All Keys', count: summary?.todayTotal },
              { id: 'TENANT_DEDICATED', label: '🔑 Tenant Own Keys', count: summary?.keyModes?.TENANT_DEDICATED, color: 'emerald' },
              { id: 'PLATFORM_MASTER', label: '⚡ Platform Master Key', count: summary?.keyModes?.PLATFORM_MASTER, color: 'cyan' },
              { id: 'AUTO_SWITCH_FALLBACK', label: '🔄 Auto-Switched', count: summary?.todayFallbacks, color: 'amber' },
            ].map(k => (
              <button
                key={k.id}
                onClick={() => setSelectedKeyMode(k.id)}
                className={`px-3 py-1.5 rounded-xl text-xs font-mono font-bold transition-all flex items-center gap-1.5 ${
                  selectedKeyMode === k.id
                    ? k.color === 'emerald'
                      ? 'bg-emerald-500/25 text-emerald-300 border border-emerald-500/70 shadow-[0_0_12px_rgba(16,185,129,0.3)]'
                      : k.color === 'cyan'
                      ? 'bg-cyan-500/25 text-cyan-300 border border-cyan-500/70 shadow-[0_0_12px_rgba(6,182,212,0.3)]'
                      : k.color === 'amber'
                      ? 'bg-amber-500/25 text-amber-300 border border-amber-500/70 shadow-[0_0_12px_rgba(245,158,11,0.3)]'
                      : 'bg-white/15 text-white border border-white/40 shadow-sm'
                    : 'bg-black/40 text-slate-400 border border-white/5 hover:text-white hover:border-white/20'
                }`}
              >
                <span>{k.label}</span>
                {typeof k.count === 'number' && (
                  <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-mono ${
                    selectedKeyMode === k.id ? 'bg-white/20 text-white' : 'bg-white/5 text-slate-400'
                  }`}>
                    {k.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Right: Search & Stream Controls */}
          <div className="flex items-center gap-2 w-full lg:w-auto">
            <div className="relative flex-1 lg:w-56">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                type="text"
                placeholder="Search tenant, IP, phone..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full bg-black/60 border border-white/10 rounded-xl pl-9 pr-3 py-1.5 text-xs text-slate-200 font-mono outline-none focus:border-cyan-500/50 placeholder:text-slate-600"
              />
            </div>

            <button
              onClick={() => setIsPaused(p => !p)}
              className={`p-2 rounded-xl border transition-all ${
                isPaused
                  ? 'bg-amber-500/20 border-amber-500/50 text-amber-300'
                  : 'bg-black/40 border-white/10 text-slate-400 hover:text-white'
              }`}
              title={isPaused ? 'Resume live stream' : 'Pause live stream'}
            >
              {isPaused ? <Play size={14} /> : <Pause size={14} />}
            </button>

            <button
              onClick={() => setEvents([])}
              className="p-2 rounded-xl bg-black/40 border border-white/10 text-slate-400 hover:text-rose-400 transition-all"
              title="Clear buffer"
            >
              <Trash2 size={14} />
            </button>
          </div>
        </div>

        {/* Row 2: Channel & Status Filters */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3 pt-2.5 border-t border-white/5">
          {/* Channel Filters */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[11px] font-mono font-bold text-slate-400 mr-1 flex items-center gap-1">
              <SlidersHorizontal size={13} /> Channel:
            </span>
            {[
              { id: 'all', label: 'All Channels' },
              { id: 'whatsapp', label: 'WhatsApp' },
              { id: 'sip_voice', label: 'SIP Voice' },
              { id: 'pos', label: 'POS' },
              { id: 'personal_luna', label: 'Personal' },
              { id: 'public_web', label: 'Public Web' },
              { id: 'widget', label: 'Tenant Web' },
            ].map(c => (
              <button
                key={c.id}
                onClick={() => setSelectedChannel(c.id)}
                className={`px-3 py-1 rounded-xl text-xs font-mono font-bold transition-all ${
                  selectedChannel === c.id
                    ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/50 shadow-sm'
                    : 'bg-black/40 text-slate-400 border border-white/5 hover:text-white'
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>

          {/* Status Filters */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[11px] font-mono font-bold text-slate-400 mr-1">Status:</span>
            {[
              { id: 'all', label: 'All' },
              { id: 'fallback', label: '⚡ Fallbacks Only' },
              { id: 'error', label: '🚨 Errors & 429' },
            ].map(s => (
              <button
                key={s.id}
                onClick={() => setSelectedStatus(s.id)}
                className={`px-3 py-1 rounded-xl text-xs font-mono font-bold transition-all ${
                  selectedStatus === s.id
                    ? s.id === 'fallback'
                      ? 'bg-amber-500/20 text-amber-300 border border-amber-500/50'
                      : s.id === 'error'
                      ? 'bg-rose-500/20 text-rose-300 border border-rose-500/50'
                      : 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/50'
                    : 'bg-black/40 text-slate-400 border border-white/5 hover:text-white'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── LIVE STREAM EVENT FEED TABLE ── */}
      <div className="bg-[#090D18] border border-cyan-500/20 rounded-2xl overflow-hidden shadow-xl">
        <div className="px-5 py-3.5 border-b border-white/5 bg-slate-950/60 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Radio size={15} className={`text-cyan-400 ${isConnected ? 'animate-pulse' : ''}`} />
            <h3 className="text-sm font-black font-mono text-white tracking-wider uppercase">
              REAL-TIME REQUEST FEED
            </h3>
            <span className="px-2 py-0.5 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 font-mono text-[10px]">
              {filteredEvents.length} shown · {summary?.lifetimeTotal ?? filteredEvents.length} lifetime recorded
            </span>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-[10px] font-mono text-slate-400 bg-white/5 border border-white/10 px-2.5 py-1 rounded-xl flex items-center gap-1.5">
              <CheckCircle2 size={12} className="text-emerald-400" />
              <span>MySQL Lifetime Storage Active</span>
            </span>

            {summary?.lifetimeTokens ? (
              <span className="text-[10px] font-mono text-slate-300 bg-purple-500/10 border border-purple-500/30 px-2.5 py-1 rounded-xl">
                Tokens: <strong className="text-purple-300">{summary.lifetimeTokens > 1000000 ? (summary.lifetimeTokens / 1000000).toFixed(2) + 'M' : summary.lifetimeTokens.toLocaleString()}</strong>
              </span>
            ) : null}

            {isPaused && (
              <span className="px-2.5 py-0.5 rounded-lg bg-amber-500/20 border border-amber-500/40 text-amber-300 font-mono text-[10px] font-bold">
                STREAM PAUSED
              </span>
            )}
          </div>
        </div>

        <div className="overflow-x-auto max-h-[600px] overflow-y-auto divide-y divide-white/5 font-sans">
          {filteredEvents.length === 0 ? (
            <div className="text-center py-16 text-slate-500 font-mono text-xs">
              No telemetry events matching current filters. Send a WhatsApp message or initiate a call to watch requests stream live!
            </div>
          ) : (
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="border-b border-white/5 bg-slate-900/60 text-[10px] font-mono font-black text-slate-400 uppercase tracking-wider">
                  <th className="py-3 px-4">Time</th>
                  <th className="py-3 px-4">Channel</th>
                  <th className="py-3 px-4">Tenant / Store Account</th>
                  <th className="py-3 px-4">Caller ID / IP</th>
                  <th className="py-3 px-4">AI Key Mode</th>
                  <th className="py-3 px-4 text-right">Duration / Latency</th>
                  <th className="py-3 px-4 text-center">Status</th>
                  <th className="py-3 px-4 text-right">Inspect</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5 font-mono">
                {filteredEvents.map(event => (
                  <tr
                    key={event.id}
                    className={`hover:bg-white/[0.03] transition-colors ${
                      event.autoSwitched ? 'bg-amber-500/[0.04]' : event.status === 'error' ? 'bg-rose-500/[0.04]' : ''
                    }`}
                  >
                    {/* Timestamp */}
                    <td className="py-3 px-4 text-slate-400 text-[11px] whitespace-nowrap">
                      {new Date(event.timestamp).toLocaleTimeString('en-US', { hour12: true })}
                    </td>

                    {/* Channel */}
                    <td className="py-3 px-4 whitespace-nowrap">
                      {getChannelBadge(event.channel)}
                    </td>

                    {/* Tenant Account & Exact Session */}
                    <td className="py-3 px-4 font-sans max-w-[220px]">
                      <div className="font-bold text-slate-200 truncate" title={event.tenantName || 'Public Kangavena'}>
                        {event.tenantName || 'Public Kangavena'}
                      </div>
                      <div className="text-[10px] font-mono text-slate-500 truncate flex items-center gap-1.5 mt-0.5">
                        <span className="truncate">{event.tenantId ? `Tenant: ${event.tenantId}` : event.storeName || 'System'}</span>
                        <span className="text-slate-600">·</span>
                        <span 
                          className="text-cyan-400/90 font-mono font-medium px-1 rounded bg-cyan-950/40 border border-cyan-800/30 whitespace-nowrap text-[9px]"
                          title={`Exact Session ID: ${event.id}`}
                        >
                          {event.id.startsWith('sess_') ? `sess:${event.id.slice(5, 13)}` : event.id.startsWith('sip_') ? `sip:${event.id.slice(4, 12)}` : event.id.startsWith('tel_') ? `tel:${event.id.slice(4, 12)}` : event.id.slice(0, 10)}
                        </span>
                      </div>
                    </td>

                    {/* Caller ID / Client IP */}
                    <td className="py-3 px-4 whitespace-nowrap">
                      <div className="text-cyan-300 font-bold text-xs truncate max-w-[150px]">
                        {event.callerId ? (
                          event.callerId.startsWith('+') ? event.callerId : `+${event.callerId}`
                        ) : (
                          'Anonymous'
                        )}
                      </div>
                      <div className="text-[10px] text-slate-500 flex items-center gap-1">
                        <Globe size={10} /> {event.clientIp}
                      </div>
                    </td>

                    {/* AI Key Mode */}
                    <td className="py-3 px-4 whitespace-nowrap">
                      {event.autoSwitched || event.keyMode === 'AUTO_SWITCH_FALLBACK' ? (
                        <div
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-500/20 border border-amber-500/40 text-amber-300 text-[10px] font-bold"
                          title={event.fallbackReason || 'Switched from tenant key to master key'}
                        >
                          <RefreshCw size={10} className="animate-spin text-amber-400" />
                          <span>AUTO-SWITCHED</span>
                        </div>
                      ) : event.keyMode === 'TENANT_DEDICATED' ? (
                        <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-[10px] font-bold">
                          <Key size={10} className="text-emerald-400" />
                          <span>DEDICATED</span>
                        </div>
                      ) : (
                        <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-slate-800 border border-slate-700 text-slate-400 text-[10px]">
                          <Zap size={10} />
                          <span>MASTER</span>
                        </div>
                      )}
                    </td>

                    {/* Duration / Latency */}
                    <td className="py-3 px-4 text-right text-xs whitespace-nowrap font-mono">
                      {formatDurationOrLatency(event)}
                    </td>

                    {/* Status */}
                    <td className="py-3 px-4 text-center whitespace-nowrap">
                      {getStatusBadge(event)}
                    </td>

                    {/* Action */}
                    <td className="py-3 px-4 text-right whitespace-nowrap">
                      <button
                        onClick={() => setInspectingEvent(event)}
                        className="px-2.5 py-1 rounded-lg bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 text-cyan-300 text-[11px] font-bold font-mono transition-all flex items-center gap-1 ml-auto"
                      >
                        Inspect <ChevronRight size={12} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── EVENT INSPECTION DRAWER / MODAL ── */}
      {inspectingEvent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-md p-4">
          <div className="bg-[#090D18] border border-cyan-500/40 rounded-3xl p-6 w-full max-w-2xl shadow-[0_0_80px_rgba(6,182,212,0.25)] flex flex-col max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between pb-4 border-b border-white/10 mb-4">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center text-cyan-400">
                  <Activity size={18} />
                </div>
                <div>
                  <h3 className="text-base font-black font-mono text-white tracking-wide">
                    REQUEST TELEMETRY INSPECTOR
                  </h3>
                  <p className="text-[11px] font-mono text-cyan-400/80">{inspectingEvent.id}</p>
                </div>
              </div>
              <button
                onClick={() => setInspectingEvent(null)}
                className="p-1.5 rounded-xl bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white transition-all"
              >
                <X size={18} />
              </button>
            </div>

            {/* Content Body */}
            <div className="flex-1 overflow-y-auto space-y-4 pr-1 text-xs">
              {/* Auto-Switch / Fallback Banner if applicable */}
              {inspectingEvent.autoSwitched && (
                <div className="p-3.5 rounded-2xl bg-amber-500/10 border border-amber-500/40 text-amber-300 space-y-1">
                  <div className="flex items-center gap-2 font-mono font-black text-xs uppercase tracking-wider">
                    <RefreshCw size={14} className="text-amber-400 animate-spin" />
                    <span>Autonomous Self-Healing Key Fallback Executed</span>
                  </div>
                  <p className="text-xs text-amber-200/90 font-mono">
                    {inspectingEvent.fallbackReason || 'Tenant dedicated key failed; request automatically switched to Platform Master Key.'}
                  </p>
                </div>
              )}

              {/* Error Banner if applicable */}
              {inspectingEvent.errorMessage && (
                <div className="p-3.5 rounded-2xl bg-rose-500/10 border border-rose-500/40 text-rose-300 space-y-1">
                  <div className="flex items-center gap-2 font-mono font-black text-xs uppercase tracking-wider">
                    <AlertTriangle size={14} className="text-rose-400" />
                    <span>Error / Rate Limit Details</span>
                  </div>
                  <p className="text-xs text-rose-200/90 font-mono whitespace-pre-wrap">
                    {inspectingEvent.errorMessage}
                  </p>
                </div>
              )}

              {/* Metadata Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 font-mono">
                <div className="bg-black/50 border border-white/5 rounded-xl p-3">
                  <span className="text-[10px] text-slate-500 uppercase tracking-widest block mb-0.5">Channel</span>
                  <div className="mt-1">{getChannelBadge(inspectingEvent.channel)}</div>
                </div>

                <div className="bg-black/50 border border-white/5 rounded-xl p-3">
                  <span className="text-[10px] text-slate-500 uppercase tracking-widest block mb-0.5">Status</span>
                  <div className="mt-1">{getStatusBadge(inspectingEvent)}</div>
                </div>

                <div className="bg-black/50 border border-white/5 rounded-xl p-3">
                  <span className="text-[10px] text-slate-500 uppercase tracking-widest block mb-0.5">AI Key Source</span>
                  <div className="mt-1">
                    {inspectingEvent.autoSwitched || inspectingEvent.keyMode === 'AUTO_SWITCH_FALLBACK' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-500/20 text-amber-300 text-[11px] font-bold">
                        <RefreshCw size={11} className="animate-spin text-amber-400" /> Auto-Switched
                      </span>
                    ) : inspectingEvent.keyMode === 'TENANT_DEDICATED' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 text-[11px] font-bold">
                        <Key size={11} className="text-emerald-400" /> Tenant Own Key
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-cyan-500/20 text-cyan-300 text-[11px] font-bold">
                        <Zap size={11} className="text-cyan-400" /> Platform Master Key
                      </span>
                    )}
                  </div>
                </div>

                <div className="bg-black/50 border border-white/5 rounded-xl p-3">
                  <span className="text-[10px] text-slate-500 uppercase tracking-widest block mb-0.5">Tenant Account</span>
                  <p className="text-xs font-bold text-white truncate">{inspectingEvent.tenantName || 'None'}</p>
                  <p className="text-[10px] text-slate-400">{inspectingEvent.tenantId || 'Public'}</p>
                </div>

                <div className="bg-black/50 border border-white/5 rounded-xl p-3">
                  <span className="text-[10px] text-slate-500 uppercase tracking-widest block mb-0.5">Caller ID / IP</span>
                  <p className="text-xs font-bold text-cyan-300">{inspectingEvent.callerId || 'None'}</p>
                  <p className="text-[10px] text-slate-400">{inspectingEvent.clientIp} ({inspectingEvent.device || 'Desktop'})</p>
                </div>

                <div className="bg-black/50 border border-white/5 rounded-xl p-3">
                  <span className="text-[10px] text-slate-500 uppercase tracking-widest block mb-0.5">Duration / Latency</span>
                  <div className="text-xs font-bold text-slate-200">
                    {inspectingEvent.status === 'active' ? (
                      <span className="text-cyan-400 flex items-center gap-1">
                        <Clock size={12} className="animate-spin text-cyan-300" />
                        <span>Live Session In-Flight</span>
                      </span>
                    ) : inspectingEvent.latencyMs >= 1000 ? (
                      <span>{Math.round(inspectingEvent.latencyMs / 1000)}s ({inspectingEvent.latencyMs.toLocaleString()}ms)</span>
                    ) : inspectingEvent.latencyMs > 0 ? (
                      <span>{inspectingEvent.latencyMs}ms</span>
                    ) : (
                      <span className="text-slate-500">—</span>
                    )}
                  </div>
                  <p className="text-[10px] text-amber-300 font-bold mt-0.5">{inspectingEvent.tokensUsed.toLocaleString()} tokens</p>
                </div>
              </div>

              {/* 💬 Full Voice & Chat Conversation Transcript */}
              {transcriptLoading ? (
                <div className="bg-black/50 border border-white/5 rounded-xl p-6 flex flex-col items-center justify-center gap-2.5 text-center">
                  <RefreshCw size={18} className="text-cyan-400 animate-spin" />
                  <span className="text-xs text-slate-400 font-mono">Retrieving full multi-turn conversation transcript...</span>
                </div>
              ) : transcriptData?.messages?.length > 0 ? (
                <div className="bg-black/60 border border-cyan-500/20 rounded-xl p-3.5 space-y-3">
                  <div className="flex items-center justify-between border-b border-white/10 pb-2">
                    <div className="flex items-center gap-2">
                      <MessageSquare size={14} className="text-cyan-400" />
                      <span className="text-[11px] font-mono font-bold text-white uppercase tracking-wider">
                        Full Spoken / Chat Conversation History
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 font-mono text-[10px]">
                        {transcriptData.messages.length} Turns
                      </span>
                      {transcriptData.totalTokens > 0 && (
                        <span className="px-2 py-0.5 rounded-full bg-purple-500/10 border border-purple-500/30 text-purple-300 font-mono text-[10px]">
                          {transcriptData.totalTokens.toLocaleString()} Tokens
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2.5 max-h-[360px] overflow-y-auto pr-1">
                    {transcriptData.messages.map((m: any, idx: number) => {
                      const isLuna = m.sender === 'luna' || m.role === 'luna' || m.role === 'assistant';
                      return (
                        <div
                          key={idx}
                          className={`flex flex-col gap-1 p-3 rounded-xl border ${
                            isLuna
                              ? 'bg-slate-900/80 border-cyan-500/30 mr-4'
                              : 'bg-cyan-950/20 border-white/10 ml-4'
                          }`}
                        >
                          <div className="flex items-center justify-between text-[10px] font-mono text-slate-400">
                            <span className={`font-bold flex items-center gap-1.5 ${isLuna ? 'text-cyan-400' : 'text-slate-300'}`}>
                              {isLuna ? '✦ Luna AI (Assistant)' : `Visitor (${m.sender || 'User'})`}
                            </span>
                            <div className="flex items-center gap-2">
                              {m.tokens?.totalTokens > 0 && (
                                <span className="text-amber-400/90 text-[9px] font-mono">
                                  {m.tokens.totalTokens} tok
                                </span>
                              )}
                              <span>{m.time || ''}</span>
                            </div>
                          </div>
                          <p className="text-xs text-slate-200 font-sans whitespace-pre-wrap leading-relaxed">
                            {m.text}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <>
                  {/* Prompt Preview Fallback */}
                  {inspectingEvent.promptPreview && (
                    <div className="bg-black/50 border border-white/5 rounded-xl p-3.5 space-y-1">
                      <span className="text-[10px] font-mono text-slate-400 uppercase tracking-widest block">
                        User Query / Prompt
                      </span>
                      <p className="text-xs text-slate-200 font-sans whitespace-pre-wrap">
                        {inspectingEvent.promptPreview}
                      </p>
                    </div>
                  )}

                  {/* Response Preview Fallback */}
                  {inspectingEvent.responsePreview && (
                    <div className="bg-black/50 border border-white/5 rounded-xl p-3.5 space-y-1">
                      <span className="text-[10px] font-mono text-emerald-400 uppercase tracking-widest block">
                        Luna AI Response
                      </span>
                      <p className="text-xs text-slate-300 font-sans whitespace-pre-wrap">
                        {inspectingEvent.responsePreview}
                      </p>
                    </div>
                  )}
                </>
              )}

              {/* User-Agent */}
              {inspectingEvent.userAgent && (
                <div className="bg-black/50 border border-white/5 rounded-xl p-3 font-mono">
                  <span className="text-[10px] text-slate-500 uppercase tracking-widest block mb-1">User Agent</span>
                  <p className="text-[11px] text-slate-400 break-all">{inspectingEvent.userAgent}</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="pt-4 border-t border-white/10 mt-4 flex items-center justify-between">
              <button
                onClick={copyEventJson}
                className="px-3 py-1.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 text-xs font-mono font-bold transition-all flex items-center gap-1.5"
              >
                {copied ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
                {copied ? 'Copied JSON' : 'Copy JSON'}
              </button>

              <button
                onClick={() => setInspectingEvent(null)}
                className="px-4 py-1.5 rounded-xl bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-500/40 text-cyan-300 text-xs font-mono font-bold transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default LunaLiveMonitorHUD;
